import React from 'react';
import { Drawer, DrawerBody } from '@fluentui/react-components';
import { Dismiss20Regular, Edit12Regular, Delete12Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { format } from 'date-fns';

import styles from '../TaskManagement/css/index.module.css';
import { CustomAvatar } from '../TaskManagement';
import SafeHTML from '../SafeHTML';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';

import {
    DeleteActionLogComment,
    GetActionLogComments,
    PatchActionLogComment,
    PostActionLogComment,
} from '../../redux/actionLogSlice';

const ActionLogDetailsSidebar = ({ open, onClose, entry }) => {
    const dispatch = useDispatch();
    const { actionLogComments, actionLogCommentBtnLoading } = useSelector(state => state.actionLog || { actionLogComments: [], actionLogCommentBtnLoading: false });
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team || { projectUsersAll: [] });

    const [comment, setComment] = React.useState('');
    const quillRef = React.useRef(null);
    const [editingCommentId, setEditingCommentId] = React.useState(null);
    const [editingCommentText, setEditingCommentText] = React.useState('');

    React.useEffect(() => {
        if (entry?.id) {
            const projectId = localStorage.getItem('project_id');
            if (projectId) dispatch(GetActionLogComments({ entryId: entry.id, projectId }));
        }
    }, [entry, dispatch]);

    const getUserName = id => {
        const found = projectUsersAll?.find(u => u?.value?.user_id === id);
        if (found?.value?.full_name) return found.value.full_name;
        if (user?.id === id) return user.full_name;
        return '';
    };

    const stripText = html => {
        if (!html) return '';
        return String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    };

    const handleSubmit = () => {
        if (!comment.trim() || !entry?.id) return;
        dispatch(PostActionLogComment({ entry: entry.id, comment }));
        setComment('');
        if (quillRef.current) quillRef.current.getEditor().setText('');
    };

    const handleEditComment = item => {
        setEditingCommentId(item.id);
        setEditingCommentText(item.comment);
    };

    const handleSaveEdit = () => {
        if (!editingCommentText.trim() || !editingCommentId) return;
        dispatch(PatchActionLogComment({ commentId: editingCommentId, body: { comment: editingCommentText } }));
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    return (
        <Drawer
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(e, data) => {
                if (!data.open) onClose();
            }}
            size="medium">
            <DrawerBody>
                <div className="mt-4 d-flex justify-content-between">
                    <div>
                        <p className={styles.sidebarWsTagLabel + ' mb-0'}>
                            {entry?.workstream_repr || ''}
                            {entry?.source_repr ? ` / ${entry.source_repr}` : ''}
                        </p>
                        <p className={styles.sidebarTaskText}>{stripText(entry?.action_text)}</p>
                    </div>
                    <div className="ms-2">
                        <Dismiss20Regular role="button" color="var(--Neutral-Gray-6, #989BAD)" onClick={onClose} />
                    </div>
                </div>

                <div className="mt-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <div>
                            <CustomAvatar name={entry?.created_by_name} size={20} />
                        </div>
                        <span className={styles.sidebarWsTagLabel + ' mb-0'}>
                            {entry?.created ? `Added on ${format(new Date(entry.created), 'dd/MM/yyyy')}` : 'Added on -- --'}
                        </span>
                    </div>
                </div>

                <div className={styles.divider} />

                <p className={styles.sidebarWsTagLabel}>Comments</p>

                {actionLogComments?.length > 0 && (
                    <div className="mt-4">
                        {actionLogComments.map(item => (
                            <div key={item?.id} className="mb-4">
                                {editingCommentId === item?.id ? (
                                    <div>
                                        <ReactQuill useSemanticHTML={false}
                                            placeholder={'Edit comment'}
                                            modules={{
                                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                            }}
                                            theme="snow"
                                            className="mt-1"
                                            defaultValue={editingCommentText}
                                            onChange={t => {
                                                if (t === '<p><br></p>') setEditingCommentText('');
                                                else setEditingCommentText(t);
                                            }}
                                        />
                                        <div className="mt-2 d-flex gap-2">
                                            <button onClick={handleSaveEdit} disabled={!editingCommentText.trim() || actionLogCommentBtnLoading} type="button" className="btn btn-primary btn-sm">
                                                Save
                                            </button>
                                            <button onClick={handleCancelEdit} type="button" className="btn btn-secondary btn-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <SafeHTML html={item?.comment} className={styles.commentText} tag="p" />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className={styles.sidebarWsTagLabel + ' mb-0 mt-1'}>
                                                {getUserName(item?.created_by)} {item?.created ? format(new Date(item.created), 'dd/MM/yyyy') : ''}
                                            </div>
                                            {item?.created_by === user?.id && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <DoubleConfirmationModal
                                                        message="Deleting a comment will permanently remove it from the Action Log."
                                                        handleAction={() => {
                                                            if (actionLogCommentBtnLoading) return;
                                                            if (!item?.id || !entry?.id) return;

                                                            if (editingCommentId === item.id) {
                                                                setEditingCommentId(null);
                                                                setEditingCommentText('');
                                                            }

                                                            dispatch(DeleteActionLogComment({ commentId: item.id, entryId: entry.id }));
                                                        }}>
                                                        <Delete12Regular
                                                            cursor="pointer"
                                                            color="var(--Brand-Secondary)"
                                                            style={{ opacity: actionLogCommentBtnLoading ? 0.5 : 1, pointerEvents: actionLogCommentBtnLoading ? 'none' : 'auto' }}
                                                        />
                                                    </DoubleConfirmationModal>
                                                    <div>
                                                        <Edit12Regular
                                                            cursor="pointer"
                                                            onClick={() => handleEditComment(item)}
                                                            color="var(--Brand-Secondary)"
                                                            style={{ opacity: actionLogCommentBtnLoading ? 0.5 : 1, pointerEvents: actionLogCommentBtnLoading ? 'none' : 'auto' }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-2">
                    <ReactQuill useSemanticHTML={false}
                        ref={quillRef}
                        placeholder={'Add a comment'}
                        modules={{
                            toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                        }}
                        theme="snow"
                        className="mt-1"
                        defaultValue={comment}
                        onChange={t => {
                            if (t === '<p><br></p>') setComment('');
                            else setComment(t);
                        }}
                    />
                </div>
                {comment && (
                    <div className="mt-4 d-flex justify-content-between align-items-center">
                        <button onClick={handleSubmit} disabled={!comment || actionLogCommentBtnLoading} type="button" className="btn btn-primary btn-sm">
                            Add Comment {actionLogCommentBtnLoading && <span className="spinner-border spinner-border-sm ms-1" />}
                        </button>
                    </div>
                )}
            </DrawerBody>
        </Drawer>
    );
};

export default ActionLogDetailsSidebar;

